import React, { useState, useRef } from 'react';
import { LuUpload, LuX, LuPlus } from 'react-icons/lu';
import Papa from 'papaparse';
import { toast } from 'react-toastify';

/**
 * Bulk entry component for serial numbers or MAC addresses.
 * Supports: comma-separated paste, CSV upload (single column), manual add.
 *
 * Props:
 *   label - "Serial Numbers" or "MAC Addresses"
 *   values - array of current values
 *   onChange - (newValues: string[]) => void
 *   maxCount - optional max number of entries
 *   placeholder - input placeholder text
 */
const BulkIdentifierInput = ({ label, values = [], onChange, maxCount, placeholder = 'Enter value' }) => {
  const [inputValue, setInputValue] = useState('');
  const fileInputRef = useRef(null);

  const addValues = (newVals) => {
    const cleaned = newVals
      .map(v => (typeof v === 'string' ? v.trim() : ''))
      .filter(v => v && !values.includes(v));

    if (cleaned.length === 0) return;

    const combined = [...values, ...cleaned];
    const limited = maxCount ? combined.slice(0, maxCount) : combined;
    onChange(limited);

    if (maxCount && combined.length > maxCount) {
      toast.info(`Limited to ${maxCount} entries (matching quantity)`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (!inputValue.trim()) return;

      if (inputValue.includes(',')) {
        addValues(inputValue.split(','));
      } else {
        addValues([inputValue]);
      }
      setInputValue('');
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    // Split on commas, newlines, tabs
    const vals = pasted.split(/[,\n\t]+/);
    addValues(vals);
    setInputValue('');
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large (max 2 MB)');
      e.target.value = '';
      return;
    }

    Papa.parse(file, {
      complete: (results) => {
        const vals = results.data.flat().map(v => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
        addValues(vals);
        toast.success(`Imported ${vals.length} ${label.toLowerCase()}`);
      },
      error: () => toast.error('Error parsing CSV file')
    });

    e.target.value = '';
  };

  const removeValue = (index) => {
    const next = values.filter((_, i) => i !== index);
    onChange(next);
  };

  const clearAll = () => onChange([]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          {label} {maxCount && <span className="text-gray-400 font-normal">({values.length}/{maxCount})</span>}
        </label>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-indigo-600 hover:text-indigo-800 text-xs flex items-center"
          >
            <LuUpload className="w-3 h-3 mr-1" />
            Upload CSV
          </button>
          {values.length > 0 && (
            <button type="button" onClick={clearAll} className="text-red-500 hover:text-red-700 text-xs">
              Clear all
            </button>
          )}
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleCSVUpload} className="hidden" />

      {/* Input field */}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 pr-8 text-sm focus:ring-indigo-500 focus:border-indigo-500"
          placeholder={values.length === 0 ? `Paste comma-separated ${label.toLowerCase()}, or type and press Enter` : placeholder}
          disabled={maxCount && values.length >= maxCount}
        />
        {inputValue && (
          <button
            type="button"
            onClick={() => { addValues(inputValue.includes(',') ? inputValue.split(',') : [inputValue]); setInputValue(''); }}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-500 hover:text-indigo-700"
          >
            <LuPlus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tags display */}
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
          {values.map((val, index) => (
            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700">
              {val}
              <button type="button" onClick={() => removeValue(index)} className="ml-1 text-gray-400 hover:text-red-500">
                <LuX className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkIdentifierInput;
